import { useEffect, useMemo, useState } from 'react';
import { fetchCoverage, type CoverageReading } from '@/lib/coverage-client';
import { countByStatus, toMapJurisdictions, type CoverageStatus } from '@/data/coverage';
import type { MapJurisdiction } from '@sica/ui';

export interface LiveCoverage {
  isLoading: boolean;
  /** `false` cuando el backend no contestó: la página lo dice, no lo tapa. */
  isAvailable: boolean;
  jurisdictions: MapJurisdiction[];
  counts: Record<CoverageStatus, number>;
  total: number;
}

const NO_COUNTS: Record<CoverageStatus, number> = {
  covered: 0,
  partial: 0,
  citable: 0,
  pending: 0,
};

/**
 * Una sola consulta por carga, compartida entre los componentes que la piden.
 *
 * La página de jurisdicciones muestra el mapa y, más abajo, cuántas hay en cada
 * estado: son dos lecturas de la misma medición, y sin esto serían dos pedidos
 * que además podrían discrepar entre sí.
 */
let inflight: Promise<CoverageReading> | null = null;

function load(): Promise<CoverageReading> {
  inflight ??= fetchCoverage();
  return inflight;
}

/** Solo para los tests: la memoria compartida no debe cruzar de un caso a otro. */
export function resetCoverageCache(): void {
  inflight = null;
}

/**
 * La cobertura federal medida. Sin reintentos a propósito: un reintento
 * silencioso convertiría "no pudimos consultar" en un estado que parpadea, y la
 * página prefiere decirlo.
 */
export function useLiveCoverage(): LiveCoverage {
  const [reading, setReading] = useState<CoverageReading | null>(null);

  useEffect(() => {
    let cancelled = false;
    void load().then((result) => {
      if (!cancelled) setReading(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    if (reading === null) {
      return { isLoading: true, isAvailable: false, jurisdictions: [], counts: NO_COUNTS, total: 0 };
    }
    if (reading.status === 'unavailable') {
      return { isLoading: false, isAvailable: false, jurisdictions: [], counts: NO_COUNTS, total: 0 };
    }
    const jurisdictions = toMapJurisdictions(reading.jurisdictions);
    return {
      isLoading: false,
      isAvailable: true,
      jurisdictions,
      counts: countByStatus(jurisdictions),
      total: jurisdictions.length,
    };
  }, [reading]);
}
